import React, { useEffect, useMemo, useState } from 'react';
import theme from './BuyerUnitChat.module.scss';
import { Trans } from '@lingui/react/macro';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { Breadcrumbs, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { CommentThreadType } from '@/types';
import { orderBy } from 'lodash-es';
import { useIsMobile } from '@/utils/hooks';
import { RouteComponentProps } from 'react-router';
import { CreateCommentThreadModal } from '@/components/CreateCommentThreadModal';
import BuyerChat from './BuyerChat';
import { getProjectHeaderImageUrl } from '@/services/projects';
import { CommentThreadList } from '@ygtechnologies/bygr-frontend-chat';
import BuyerPageTitle from '../BuyerPageTitle/BuyerPageTitle';

interface BuyerUnitChatProps extends RouteComponentProps<any> {
    activeChatId?: string;
}

const BuyerUnitChat: React.FC<BuyerUnitChatProps> = ({ activeChatId, history }) => {
    const [createThreadIsOpen, setCreateThreadIsOpen] = useState(false);

    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions!.residentialUnitId);
    const threads = useSelector((state: IState) => state.buyer.commentThreads);
    const isMobile = useIsMobile();

    const projectData = useSelector((state: IState) => state.buyer.projectData);
    const relevantThreads = useMemo(() => {
        const filteredThreads = threads.filter((t) => t.type === CommentThreadType.ResidentialUnit);
        return orderBy(filteredThreads, [(o) => o.lastMessageDate || '', (o) => o.threadId], ['desc', 'asc']);
    }, [threads]);

    const activeThread = activeChatId ? relevantThreads.find((x) => x.threadId === +activeChatId) : null;

    useEffect(() => {
        if (activeChatId && !activeThread) {
            history.replace(`/chat`);
        }

        if (!activeChatId && relevantThreads.length > 0 && !isMobile) {
            history.replace(`/chat/${relevantThreads[0].threadId}`);
        }
    }, [relevantThreads, activeChatId, activeThread, isMobile, history]);

    const handleNewThreadCreated = async (threadId: number) => {
        // await loadData();
        history.push(`/chat/${threadId}`);
    };

    if (!relevantThreads || !projectData) {
        return <LoadingIndicator />;
    }

    const showBackgroundImage = !isMobile && projectData?.showBackgroundImageOnMessages;

    return (
        <>
            <BuyerPageTitle transparent={showBackgroundImage}>
                <Trans>Messages</Trans>
            </BuyerPageTitle>
            <div className={theme.outerContainer}>
                {showBackgroundImage && (
                    <div className={theme.backgroundImage}>
                        <img alt="Background" src={getProjectHeaderImageUrl(projectData.projectId)} />
                    </div>
                )}
                <div className={theme.container}>
                    {(!isMobile || !activeChatId) && (
                        <div className={theme.threads}>
                            <CommentThreadList
                                hrefGenerator={(thread) => `/chat/${thread.threadId}`}
                                selectedThreadId={activeChatId ? +activeChatId : null}
                                threads={relevantThreads}
                                hideFollowers
                                classNames={{
                                    thread: theme.thread,
                                }}
                            />
                        </div>
                    )}
                    {activeChatId && (
                        <div className="full-width">
                            {isMobile && (
                                <Breadcrumbs>
                                    <Breadcrumbs.Link to="/chat">
                                        <Trans>Messages</Trans>
                                    </Breadcrumbs.Link>

                                    <Breadcrumbs.Item>{activeThread?.name}</Breadcrumbs.Item>
                                </Breadcrumbs>
                            )}
                            <BuyerChat threadId={+activeChatId} className={theme.chat} wrapperClassName={theme.wrapper} />
                        </div>
                    )}
                </div>
            </div>

            <CreateCommentThreadModal
                onCreate={handleNewThreadCreated}
                residentialUnitId={residentialUnitId}
                isOpen={createThreadIsOpen}
                toggle={() => setCreateThreadIsOpen(false)}
            />
        </>
    );
};

export default BuyerUnitChat;
